import React from "react";
import SmsLegalShell from "./SmsLegalShell";

/**
 * SMS / 10DLC Privacy Policy for Life Foundation
 * Public URL: /sms/privacy
 *
 * ⚠️ TWILIO / TCR COMPLIANCE — DO NOT REMOVE OR REWORD THE FOLLOWING:
 *   1. Section 4 ("Mobile information sharing") MUST contain the exact
 *      sentence:
 *        "Mobile information will not be shared with third parties or
 *         affiliates for marketing or promotional purposes."
 *      Carrier / TCR reviewers grep for this language verbatim. Removing
 *      it will cause every A2P 10DLC campaign tied to this brand to fail
 *      vetting.
 *   2. The SMS opt-in / opt-out / HELP language in §3 and §5 must remain
 *      consistent with the registered campaign sample messages.
 */
export default function SmsPrivacyPolicy() {
  // Stable "Last Updated" date so the document does not appear to change
  // on every page load (reviewers and end users find that suspicious).
  const lastUpdated = "February 12, 2026";

  return (
    <SmsLegalShell
      testId="sms-privacy-page"
      eyebrow="SMS Program · Privacy"
      title="Privacy Policy"
      lastUpdated={lastUpdated}
    >
      <p>
        <strong>Life Foundation</strong> (“we,” “us,” or “our”), a verified
        non‑profit corporation registered in the United States (EIN
        99‑3187855), operates a case‑management SMS notification program
        under the DBA <em>The Exchange Alliance</em>. This Privacy Policy
        explains what personal information we collect from participants in our
        SMS program, how we use it, how we protect it, and the choices you
        have. By enrolling in our SMS program you agree to the data practices
        described in this policy.
      </p>

      <h2>1. Who this policy covers</h2>
      <p>
        This policy applies to residents, clients, staff, volunteers, and
        community partners who have opted in to receive text messages from
        Life Foundation / The Exchange Alliance. It does not cover any
        third‑party websites linked from our messages — those are governed
        by their own privacy policies.
      </p>

      <h2>2. Information we collect</h2>
      <p>
        We only collect the minimum information required to deliver our SMS
        case‑management service:
      </p>
      <ul>
        <li>
          <strong>Mobile phone number</strong> — the U.S. phone number you
          provide on the intake form or staff onboarding screen.
        </li>
        <li>
          <strong>Name &amp; case identifier</strong> — to personalize
          messages (e.g., “Hi Maria, your case manager…”).
        </li>
        <li>
          <strong>Opt‑in record</strong> — the date, time, and method of
          your consent (paper intake form, signed digital form, or
          in‑platform checkbox), retained as proof of consent.
        </li>
        <li>
          <strong>Message delivery metadata</strong> — message ID, delivery
          status, timestamp, and any opt‑out keywords you reply with. We do
          <em> not</em> retain the content of inbound messages other than
          standard keywords (STOP, HELP, START).
        </li>
      </ul>

      <h2>3. How we use your information</h2>
      <ul>
        <li>
          To send transactional SMS notifications you have opted in to
          receive: appointment reminders, document requests, housing
          application updates, case manager assignments, and operational
          compliance alerts.
        </li>
        <li>
          To respond to your replies of <strong>HELP</strong> with support
          information.
        </li>
        <li>
          To honor your reply of <strong>STOP</strong> by immediately
          removing your number from the program.
        </li>
        <li>
          To maintain audit logs as required by carriers, The Campaign
          Registry (TCR), and our internal compliance program.
        </li>
      </ul>

      <h2>4. Mobile information sharing</h2>
      <p>
        <strong>
          Mobile information will not be shared with third parties or
          affiliates for marketing or promotional purposes.
        </strong>{" "}
        Information sharing to subcontractors supporting our SMS service
        delivery — such as our SMS delivery infrastructure (a self‑hosted
        gateway operated by The Exchange Alliance on physical hardware in
        our office; no third‑party SMS-as-a-Service vendor is used) —
        is limited to the minimum necessary to transmit messages and is
        governed by written data‑processing agreements. No mobile opt‑in
        data is sold, rented, traded, or otherwise transferred to third
        parties or affiliates for marketing purposes. This restriction
        applies whether the data is shared in identifiable or aggregated
        form.
      </p>

      <h2>5. Your choices &amp; opt‑out</h2>
      <p>
        <strong>
          Enrollment in our SMS program is entirely voluntary.
        </strong>{" "}
        You can receive every case‑management service The Life
        Foundation offers without ever providing a phone number or
        consenting to SMS. SMS is offered as a convenience for reminders
        and notifications only; declining has no effect on your
        eligibility, services, housing, employment placement, or any
        other benefit of the program.
      </p>
      <p>You are in control of your SMS subscription at all times:</p>
      <ul>
        <li>
          Reply <strong>STOP</strong> at any time to any message we send
          you. You will receive a single confirmation message and no further
          SMS will be sent.
        </li>
        <li>
          Reply <strong>HELP</strong> to any message to receive support
          contact information.
        </li>
        <li>
          Contact your case manager directly, or email{" "}
          <a href="mailto:will.smith@lifefoundationrebuild.com">will.smith@lifefoundationrebuild.com</a>{" "}
          to request manual removal.
        </li>
        <li>
          Standard message and data rates may apply, depending on your
          mobile carrier plan.
        </li>
      </ul>

      <h2>6. Data retention</h2>
      <p>
        We retain your phone number and opt‑in record for as long as you
        are an active participant in our program, plus a minimum of 90 days
        after opt‑out (to satisfy carrier audit and dispute‑resolution
        requirements). Delivery metadata is retained for up to 18 months
        for compliance and quality‑assurance purposes, after which it is
        deleted or anonymized.
      </p>

      <h2>7. Security</h2>
      <p>
        SMS records are stored in our access‑controlled case‑management
        platform. Production data is encrypted in transit (TLS) and at rest.
        Only authorized staff with a legitimate case‑management role can
        view participant records, and all access is logged.
      </p>

      <h2>8. Children’s privacy</h2>
      <p>
        Our SMS program is intended for adult residents, staff, and
        community partners. We do not knowingly enroll individuals under
        the age of 13. If you believe a minor has been enrolled, please
        contact us and we will remove the record immediately.
      </p>

      <h2>9. Your rights</h2>
      <p>
        Depending on your state of residence, you may have the right to
        request a copy of the personal information we hold about you,
        correct inaccuracies, or request deletion. To exercise any of these
        rights contact{" "}
        <a href="mailto:will.smith@lifefoundationrebuild.com">will.smith@lifefoundationrebuild.com</a>.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. The “Last Updated”
        date at the top of this page reflects the most recent revision.
        Material changes to the SMS program will be communicated to active
        participants by text message and/or email before they take effect.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions about this Privacy Policy or our SMS program can be sent
        to:
      </p>
      <p>
        Life Foundation<br />
        7433 Springside Drive, Myrtle Beach, SC 29588<br />
        Email:{" "}
        <a href="mailto:will.smith@lifefoundationrebuild.com">will.smith@lifefoundationrebuild.com</a>
        <br />
        Phone:{" "}
        <a href="tel:+18434242655">+1 (843) 424‑2655</a>
      </p>
    </SmsLegalShell>
  );
}
